import { useEffect, useRef, useState } from 'react'

export const TICK_MS = 40 // 25 ticks/sec
// When the queue grows (many tokens arriving at once), drain proportionally faster.
const DRAIN_DIVISOR = 10

/**
 * Animates text appearing character-by-character from fullText.
 * New characters added to fullText are queued and drained at ~25fps.
 * The drain rate self-regulates: larger queues drain faster to prevent
 * falling behind when many tokens arrive at once.
 * Resets to '' immediately when active becomes false.
 *
 * The interval self-clears when the queue empties (LLM thinking, between
 * token bursts) and re-arms when new content arrives — avoiding 25 no-op
 * timer firings per second during streaming pauses.
 */
const useTypewriter = (fullText: string, active: boolean): string => {
  const [displayed, setDisplayed] = useState('')
  const queueRef = useRef('')
  const displayedRef = useRef('')
  const prevFullTextRef = useRef('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function clearTypewriterInterval() {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  useEffect(() => {
    if (!active) {
      queueRef.current = ''
      displayedRef.current = ''
      prevFullTextRef.current = ''
      setDisplayed('')
      clearTypewriterInterval()
    }
  }, [active])

  useEffect(() => {
    const newChars = fullText.slice(prevFullTextRef.current.length)
    if (newChars) queueRef.current += newChars
    prevFullTextRef.current = fullText

    // Arm the interval only when there's work to do. Each tick drains a
    // chunk; when the queue empties, the tick clears the interval so a
    // streaming pause does not burn 25 cycles per second. The next fullText
    // change re-arms.
    if (!active || queueRef.current.length === 0 || intervalRef.current !== null) return
    intervalRef.current = setInterval(() => {
      if (!queueRef.current.length) {
        clearTypewriterInterval()
        return
      }
      const n = Math.max(1, Math.ceil(queueRef.current.length / DRAIN_DIVISOR))
      const chunk = queueRef.current.slice(0, n)
      queueRef.current = queueRef.current.slice(n)
      displayedRef.current += chunk
      setDisplayed(displayedRef.current)
    }, TICK_MS)
  }, [fullText, active])

  useEffect(() => () => clearTypewriterInterval(), [])

  return displayed
}

export default useTypewriter
